import { useContext, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Box } from '@mui/material';
import PrimarySkeleton from '../loading/PrimarySkeleton';
import { BodyText, CaptionText } from '../typography/PrimaryTypography';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';
import { getAuthHeaderForUser } from '../../../lib/auth/auth.util';
import { API_ENDPOINT } from '../../../env';

const SOURCE = 'HlsVideoViewer.tsx';

type HlsVideoViewerErrorType = 'server' | 'render';

const hls_video_viewer_error_messages: Record<
  HlsVideoViewerErrorType,
  { heading: string; action: string }
> = {
  server: {
    heading: 'This video could not be loaded.',
    action:
      'There may be an issue on our end. Please try again later or contact us if the problem persists.'
  },
  render: {
    heading: 'It seems that your browser is not able to play the video.',
    action: 'Contact your IT support about updating or changing your browser.'
  }
};

interface HlsVideoViewerProps {
  video_url: string;
  thumbnail_url?: string;
}

/**
 * Inline HLS video player. Accepts a full video URL and streams it via HLS.js.
 * @param {string} video_url Full URL to the HLS manifest.
 * @param {string | undefined} thumbnail_url Optional full URL to a thumbnail image, shown before playback begins.
 * @returns {JSX.Element}
 */
const HlsVideoViewer = ({ video_url, thumbnail_url }: HlsVideoViewerProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<HlsVideoViewerErrorType | null>(null);

  useEffect(() => {
    if (!videoRef.current) return;

    if (Hls.isSupported()) {
      const hls = new Hls({
        xhrSetup: async (xhr: XMLHttpRequest, url: string) => {
          if (url.startsWith(API_ENDPOINT)) {
            const { authorization } = await getAuthHeaderForUser();
            xhr.setRequestHeader('Authorization', authorization);
          }
        }
      });
      hls.loadSource(video_url);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          const error_type: HlsVideoViewerErrorType =
            data.type === Hls.ErrorTypes.MEDIA_ERROR ? 'render' : 'server';
          const tracking_id: string = generateUuidV4();
          log(
            'error',
            new MetadataError(
              FUNCTION_UNKNOWN_FAILURE(SOURCE, 'hls.loadSource'),
              { video_url, error_type: data.type, error_details: data.details },
              tracking_id
            )
          );
          if (error_type === 'server') {
            setFeedbackData({
              message: FEEDBACK_ACTION_FAILURE('Unable to load video', tracking_id),
              state: true,
              type: 'error'
            });
          }
          setError(error_type);
        }
      });
      return () => hls.destroy();
    }
  }, [video_url]);

  if (error) {
    const { heading, action } = hls_video_viewer_error_messages[error];
    return (
      <Box
        sx={{
          width: '100%',
          height: 450,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <BodyText>{heading}</BodyText>
        <CaptionText>{action}</CaptionText>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: 450 }}>
      {loading && (
        <PrimarySkeleton
          width="100%"
          height={450}
          variant="rounded"
          sx={{ position: 'absolute', top: 0, left: 0 }}
        />
      )}
      <video
        ref={videoRef}
        controls
        controlsList="nodownload"
        poster={thumbnail_url}
        onCanPlay={() => setLoading(false)}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 4,
          objectFit: 'contain',
          opacity: loading ? 0 : 1
        }}
      />
    </Box>
  );
};

export default HlsVideoViewer;
